import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaQuoteLeft, FaQuoteRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { RiDoubleQuotesL, RiDoubleQuotesR } from 'react-icons/ri';
import Jun from '../assets/images/jun.JPG';
import Ouyang from '../assets/images/ouyang.JPG';
import michael from '../assets/images/michael.jpeg';
import { a } from 'framer-motion/client';


const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Container = styled.section`
  padding: 4rem 0rem;
  position: relative;
  overflow: hidden;
  width: 100%;
  
  @media (min-width: 768px) {
    padding: 6rem 2rem;
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2rem;
  margin-bottom: 3rem;
  color: #e2e8f0;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 4rem;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4rem 2rem;
//   max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  flex: 0 0 calc(100% - 2rem);
  min-width: auto;
  max-width: 100%;
  opacity: ${props => props.isPartiallyVisible ? 0.3 : 1};
  transition: opacity 0.3s ease;
  
  @media (min-width: 768px) {
    flex: 0 0 calc(33.333% - 1.33rem);
    min-width: 350px;
  }
`;

// Define an array of background gradients
const cardBackgrounds = [
  'linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(139, 92, 246, 0.8))',  // Purple-Indigo
  'linear-gradient(135deg, rgba(16, 185, 129, 0.8), rgba(5, 150, 105, 0.8))',   // Green
  'linear-gradient(135deg, rgba(239, 68, 68, 0.8), rgba(185, 28, 28, 0.8))',    // Red
  'linear-gradient(135deg, rgba(59, 130, 246, 0.8), rgba(29, 78, 216, 0.8))',   // Blue
  'linear-gradient(135deg, rgba(245, 158, 11, 0.8), rgba(180, 83, 9, 0.8))',    // Orange
  'linear-gradient(135deg, rgba(236, 72, 153, 0.8), rgba(190, 24, 93, 0.8))'    // Pink
];

const CardContent = styled.div`
  position: relative;
  background: ${props => props.bgGradient || cardBackgrounds[0]};
  backdrop-filter: blur(10px);
  border-radius: 30px;
  padding: 1.5rem;
  height: 100%;
  min-height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255, 255, 255, 0.8), 
      transparent
    );
  }

  &:hover {
    transform: translateY(-10px);
    border-color: rgba(255, 255, 255, 0.3);
    box-shadow: 0 20px 40px -20px rgba(0, 0, 0, 0.2);
  }

  @media (min-width: 768px) {
    padding: 2rem;
    min-height: 300px;
    font-size: 1rem;
  }
`;

const QuoteIconLeft = styled(RiDoubleQuotesL)`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  font-size: 3rem;
  color: rgba(80, 1, 12, 0.8);
  transform: rotate(-10deg);
  
  @media (min-width: 768px) {
    font-size: 4rem;
    top: 2rem;
    left: 2rem;
  }
`;

const QuoteIconRight = styled(RiDoubleQuotesR)`
  position: absolute;
  bottom: 1.5rem;
  right: 1.5rem;
  font-size: 3rem;
  color: rgba(80, 1, 12, 0.8);
  transform: rotate(-10deg);
  
  @media (min-width: 768px) {
    font-size: 4rem;
    bottom: 2rem;
    right: 2rem;
  }
`;

const Quote = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: white;
  margin: 1rem 0;
  position: relative;
  z-index: 1;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.8;
    margin: 1.5rem 0;
  }
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(99, 102, 241, 0.1);
  width: 100%;
  
  @media (min-width: 768px) {
    gap: 1.5rem;
    margin-top: 2rem;
    padding-top: 2rem;
  }
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(99, 102, 241, 0.3);
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.1);
  }

  @media (min-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const AuthorInfo = styled.div`
  text-align: left;
`;

const AuthorName = styled.h4`
  color: #6366f1;
  font-size: 1rem;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
`;

const AuthorRole = styled.p`
  color: white;
  font-size: 0.8rem;
  margin: 0.25rem 0 0;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const testimonials = [
  {
    quote: "Morpheus Tech supported us with third-party SaaS and API integration, drastically enhancing our business efficiency and customer experience. This dynamic team demonstrated incredible adaptability, taking just two weeks to understand our business and delivering a solution that exceeded our expectations.",
    author: "Jun Xu",
    role: "CEO, La Casa Property",
    avatar: Jun,
  },
  {
    quote: "Morpheus Tech helped us re-design and implement our website in 3 weeks, which significantly improved our online presence and customer engagement.",
    author: "Ouyang",
    role: "Founder, Cercis Property",
    avatar: Ouyang,
  },
  {
    quote: "Paul single-handedly re-designed and rebuilt the CAP website during the pandemic, introducing a completely new information architecture and CRM system. The CRM he developed has significantly streamlined administrative processes, saved resources, and boosted the website’s traffic and user engagement. I believe under his leadership, the Morpheus Tech will be a disruptive force in the market.",
    author: "michael warren",
    role: "Senior Manager, CAP ANU",
    avatar: michael,
  }
];

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
  padding: 1rem 0;
  
  @media (min-width: 768px) {
    padding: 2rem 0;
  }
`;

const CarouselTrack = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  padding: 0 1rem;
  cursor: grab;
  
  @media (min-width: 768px) {
    gap: 3rem;
    padding: 0 2rem;
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const DragIndicator = styled.div`
  position: absolute;
  bottom: -2rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(99, 102, 241, 0.5);
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
  text-align: center;
  width: 100%;
  
  @media (min-width: 768px) {
    font-size: 0.8rem;
  }
  
  svg {
    animation: ${float} 2s ease-in-out infinite;
    width: 14px;
    height: 14px;
    
    @media (min-width: 768px) {
      width: 16px;
      height: 16px;
    }
  }
`;

const Testimonials = () => {
  const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 });
  const carouselRef = useRef(null);

  useEffect(() => {
    if (carouselRef.current) {
      const trackWidth = carouselRef.current.scrollWidth;
      const containerWidth = carouselRef.current.offsetWidth;
      setDragConstraints({ left: -(trackWidth - containerWidth), right: 0 });
    }
  }, []);

  return (
    <Container>
      <Title>What Our Clients Say</Title>
      <CarouselContainer>
        <CarouselTrack
          ref={carouselRef}
          drag="x"
          dragConstraints={dragConstraints}
          dragElastic={0.1}
          dragTransition={{ bounceStiffness: 600, bounceDamping: 20 }}
          whileTap={{ cursor: "grabbing" }}
        >
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              isPartiallyVisible={index === testimonials.length - 1}
            >
              <CardContent bgGradient={cardBackgrounds[index % cardBackgrounds.length]}>
                <QuoteIconLeft />
                <Quote>{testimonial.quote}</Quote>
                <QuoteIconRight />
                <AuthorSection>
                  <AuthorAvatar>
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.author} 
                    />
                  </AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{testimonial.author}</AuthorName>
                    <AuthorRole>{testimonial.role}</AuthorRole>
                  </AuthorInfo>
                </AuthorSection>
              </CardContent>
            </TestimonialCard>
          ))}
        </CarouselTrack>
      </CarouselContainer>
      <DragIndicator>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-hand-index" viewBox="0 0 16 16">
          <path d="M10.273 2.513a.5.5 0 0 1 .658.415 4 4 0 0 1 7.5 0 .5.5 0 0 1 .415.658 1.5 1.5 0 0 1 0 1.93 5.5 5.5 0 0 1-11 0 1.5 1.5 0 0 1 0-1.93.5-.5 0 0 1 .415-.658 4-4 0 0 1 7.5 0 .5.5 0 0 1 .415.658zM11 7.5a.5.5 0 0 1 .5.5v1.5a.5.5 0 0 1-1 0V8a.5.5 0 0 1 .5-.5zm-1 2a.5.5 0 0 1 .5.5v1.5a.5.5 0 0 1-1 0V10a.5.5 0 0 1 .5-.5z"/>
        </svg>
        <span>Drag to view more</span>
      </DragIndicator>
    </Container>
  );
};

export default Testimonials; 